import React from "react"
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import Loader from "@/components/Loader"

/**
 * Component that checks if the user has access to the current page
 * and redirects to an accessible page if not
 */
const CheckPageAccess: React.FC = () => {
  const location = useLocation()
  const { id: organizationId } = useParams<{ id: string }>()

  // Extract the organization-relative page path
  // (e.g., /organization/123/search-criteria/add -> search-criteria/add)
  const pathParts = location.pathname.split("/").filter(Boolean)
  const organizationIndex = pathParts.indexOf("organization")
  const pagePath =
    organizationIndex >= 0
      ? pathParts.slice(organizationIndex + 2).join("/")
      : pathParts[pathParts.length - 1] || ""

  const { accessiblePages, hasPageAccess, isLoading } =
    useOrganizationPermissions({ page: pagePath })

  // Show loader while permissions are being checked
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    )
  }

  // If user doesn't have access to this page, redirect to dashboard, inbox, or first accessible page
  if (!hasPageAccess) {
    let redirectTarget = null

    // First check if dashboard is accessible
    if (accessiblePages.includes("/dashboard")) {
      redirectTarget = "/dashboard"
    }
    // Then check if inbox is accessible
    else if (accessiblePages.includes("/inbox")) {
      redirectTarget = "/inbox"
    }
    // Otherwise use the first accessible page
    else {
      const otherAccessiblePage = accessiblePages.find((page) => {
        // Convert format (e.g., /dashboard -> dashboard)
        const pageName = page.replace(/^\//, "")
        return pageName && pageName !== pagePath
      })

      if (otherAccessiblePage) {
        redirectTarget = otherAccessiblePage
      }
    }

    if (redirectTarget && organizationId) {
      // Get the page name without leading slash
      const targetPage = redirectTarget.replace(/^\//, "")

      // Redirect to the target page within this organization
      return (
        <Navigate
          to={`/organization/${organizationId}/${targetPage}`}
          replace
        />
      )
    }

    // If no accessible pages or organization ID, redirect to organizations list
    return <Navigate to="/organizations" replace />
  }

  // If user has access, render the child route
  return <Outlet />
}

export default CheckPageAccess
